import { detectCurrentApp, getDefaultRoute } from '@qse/utils'
import { FormikHelpers } from 'formik'
import { useNavigate } from 'react-router-dom'
import * as Yup from 'yup'

import { authService } from './auth-service'

export interface UpdatePasswordData {
  oldPassword: string
  newPassword: string
  confirmPassword: string
}

// Validation schema for update password
export const updatePasswordValidationSchema = Yup.object({
  oldPassword: Yup.string()
    .required('Current password is required')
    .min(1, 'Current password is required'),
  newPassword: Yup.string()
    .required('New password is required')
    .min(8, 'Password must be at least 8 characters')
    .matches(
      /^(?=.*[a-z])(?=.*[A-Z])(?=.*\d)(?=.*[@$!%*?&])[A-Za-z\d@$!%*?&]/,
      'Password must contain at least one uppercase letter, one lowercase letter, one number, and one special character',
    ),
  confirmPassword: Yup.string()
    .oneOf([Yup.ref('newPassword')], 'Passwords must match')
    .required('Please confirm your password'),
})

export const useUpdatePassword = () => {
  const navigate = useNavigate()

  const handleUpdatePassword = async (
    values: UpdatePasswordData,
    {
      setSubmitting,
      setStatus,
      onSuccess,
    }: Partial<FormikHelpers<UpdatePasswordData>> & {
      onSuccess?: () => void
    },
  ) => {
    try {
      setStatus?.('')

      // Additional validation before API call
      if (!values.oldPassword.trim()) {
        setStatus?.('Please enter your current password.')

        return
      }

      if (!values.newPassword.trim()) {
        setStatus?.('Please enter a new password.')

        return
      }

      if (values.oldPassword === values.newPassword) {
        setStatus?.('Your new password must be different from your current password.')

        return
      }

      const result = await authService.updatePassword(values.oldPassword, values.newPassword)

      // Handle the result - all users should proceed normally after password update
      if (result.user) {
        // User doesn't have 2FA enabled, proceed normally
        onSuccess?.()
        // Role-based navigation using centralized logic
        const { appType } = detectCurrentApp()
        const defaultRoute = getDefaultRoute(result.user.userRole, appType)

        navigate(defaultRoute, { replace: true })
      } else {
        // Fallback: redirect to login
        onSuccess?.()
        navigate('/auth/login', {
          replace: true,
          state: {
            message: 'Password updated successfully! Please login with your new password.',
          },
        })
      }
    } catch (error: unknown) {
      // Type guard to check if error has properties we need
      const authError = error as { name?: string; message?: string }

      // Provide more specific error handling
      if (authError.name === 'NotAuthorizedException') {
        setStatus?.(
          'Your current password is incorrect. Please check your current password and try again.',
        )
      } else if (authError.name === 'InvalidPasswordException') {
        setStatus?.(
          'Your new password does not meet the security requirements. Please ensure it has at least 8 characters, including one uppercase letter, one lowercase letter, one number, and one special character.',
        )
      } else if (authError.name === 'LimitExceededException') {
        setStatus?.(
          'Too many password change attempts. Please wait a few minutes before trying again.',
        )
      } else if (authError.name === 'TooManyRequestsException') {
        setStatus?.(
          'Too many requests. Please wait a moment before trying to change your password again.',
        )
      } else {
        // Use the general error handler
        authService.handleError(error, 'updatePassword', setStatus)
      }
    } finally {
      setSubmitting?.(false)
    }
  }

  return {
    handleUpdatePassword,
    updatePasswordValidationSchema,
  }
}
